import React from "react";

/**
 * Catches render-time errors so one broken page doesn't blank the whole app.
 *
 * Without a boundary, React unmounts the entire tree on an uncaught render
 * error — the user sees a white screen with no explanation and no way back.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Kept as console output: there is no error-reporting service wired up,
    // and swallowing this silently would make production bugs invisible.
    console.error("Unhandled render error:", error, info?.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const isDev = process.env.NODE_ENV === "development";

    return (
      <div style={S.wrap} role="alert">
        <p style={S.badge}>Something went wrong</p>
        <h1 style={S.title}>This page didn&rsquo;t load correctly</h1>
        <p style={S.body}>
          The rest of ByteGurukul is still working. Try again, or head back to
          the homepage.
        </p>

        {isDev && this.state.error && (
          <pre style={S.details}>{String(this.state.error?.message || this.state.error)}</pre>
        )}

        <div style={S.actions}>
          <button type="button" onClick={this.handleReset} style={S.primary}>
            Try again
          </button>
          <a href="/" style={S.secondary}>Go to homepage</a>
        </div>
      </div>
    );
  }
}

const S = {
  wrap: {
    maxWidth: "560px",
    margin: "0 auto",
    padding: "clamp(48px, 9vw, 88px) 20px",
    textAlign: "center",
    fontFamily: "'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  },
  badge: {
    display: "inline-block",
    margin: 0,
    fontSize: "11.5px",
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "#B91C1C",
    background: "#FEF2F2",
    border: "1px solid #FECACA",
    borderRadius: "999px",
    padding: "5px 13px",
  },
  title: {
    margin: "16px 0 0",
    fontSize: "clamp(23px, 3.4vw, 30px)",
    fontWeight: 800,
    color: "#0F172A",
    textWrap: "balance",
  },
  body: {
    margin: "12px auto 0",
    maxWidth: "46ch",
    fontSize: "15.5px",
    lineHeight: 1.65,
    color: "#64748B",
  },
  details: {
    margin: "20px 0 0",
    padding: "12px 14px",
    textAlign: "left",
    fontFamily: "ui-monospace, Consolas, monospace",
    fontSize: "12.5px",
    color: "#334155",
    background: "#F1F5F9",
    border: "1px solid #E2E8F0",
    borderRadius: "8px",
    overflowX: "auto",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: "12px",
    marginTop: "26px",
  },
  primary: {
    padding: "12px 26px",
    fontSize: "15px",
    fontWeight: 600,
    fontFamily: "inherit",
    color: "#fff",
    background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
  },
  secondary: {
    display: "inline-flex",
    alignItems: "center",
    padding: "12px 25px",
    fontSize: "15px",
    fontWeight: 600,
    color: "#4F46E5",
    background: "#fff",
    border: "1.5px solid #E2E8F0",
    borderRadius: "12px",
    textDecoration: "none",
  },
};
